import {
  AloftArea,
  AloftPrice,
  CraftsmanCostProps,
  getCraftsmanCost,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectCompSequence } from '../orderSelectors.ts';
import { selectImprovementEntityIds, selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectCostProps } from './selectCostProps.ts';

const calculateFinishedBasementAreaCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) => {
  const improvementsForTransaction = improvements[id];
  const identification = propertyIdentification[id];

  if (isUndefined(improvementsForTransaction) || isUndefined(identification)) {
    return undefined;
  }

  const {
    basement,
    craftsmanQuality: quality,
    propertyType = 'singleFamilyResidence',
  } = improvementsForTransaction;
  const {
    address: { PostalCode: zipCode },
  } = identification;

  if (isUndefined(quality) || (quality * 10) % 1 !== 0 || quality < 1 || isUndefined(zipCode)) {
    return undefined;
  }

  const calculator = getCraftsmanCost(costProps);

  const finishedArea =
    isDefined(basement) && isDefined(basement.finishedArea)
      ? AloftArea.schema.parse(basement.finishedArea)
      : AloftArea.zero;

  return calculator.getFinishedBasementAreaCost({
    finishedArea,
    quality,
    propertyType,
    zipCode,
  });
};

const getTotalOrBreakdownFinishedBasementAreaCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
  type: 'totalCost' | 'zipOnly',
) => {
  const costOutput = calculateFinishedBasementAreaCost(
    costProps,
    improvements,
    propertyIdentification,
    id,
  );

  if (costOutput === undefined) {
    return AloftPrice.zero;
  }

  return type === 'totalCost' ? costOutput.totalCost : costOutput.multiplierBreakdown[type];
};

const getTotalFinishedBasementAreaCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) =>
  getTotalOrBreakdownFinishedBasementAreaCost(
    costProps,
    improvements,
    propertyIdentification,
    id,
    'totalCost',
  );

export const selectFinishedBasementAreaCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state, id: string) => id,
  ],
  getTotalFinishedBasementAreaCost,
);

export const selectZipOnlyFinishedBasementAreaCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state, id: string) => id,
  ],
  (costProps, improvements, propertyIdentification, id) =>
    getTotalOrBreakdownFinishedBasementAreaCost(
      costProps,
      improvements,
      propertyIdentification,
      id,
      'zipOnly',
    ),
);

export const selectFinishedBasementAreaCosts = createSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    selectImprovementEntityIds,
  ],
  (costProps, improvements, propertyIdentification, improvementEntityIds) => improvementEntityIds.reduce((acc: Record<string, AloftPrice>, propID) => {
      const cost = getTotalFinishedBasementAreaCost(
        costProps,
        improvements,
        propertyIdentification,
        propID,
      );
      if (isDefined(cost)) acc[propID] = cost;
      return acc;
    }, {}),
);

export const selectSubjectFinishedBasementAreaCostOutput = createSelector(
  [selectCostProps, selectImprovementsEntities, selectPropertyIdentificationEntities],
  (costProps, improvements, propertyIdentification) =>
    calculateFinishedBasementAreaCost(costProps, improvements, propertyIdentification, 'subject'),
);

export const selectSubjectArvFinishedBasementAreaCostOutput = createSelector(
  [selectCostProps, selectImprovementsEntities, selectPropertyIdentificationEntities],
  (costProps, improvements, propertyIdentification) =>
    calculateFinishedBasementAreaCost(costProps, improvements, propertyIdentification, 'subjectArv'),
);


export const selectFinishedBasementAreaCostDisplay = createSelector(
  [selectFinishedBasementAreaCost],
  (basementFinishedCost) => {
    if (isUndefined(basementFinishedCost)) {
      return undefined;
    }

    return basementFinishedCost.toString(true);
  },
);
